/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { serverData } from '../../organisms/HotelInformation/HotelInformation';
import { useHotelInformationServerDataStore } from '../../modules/store/hotelInformationServerDataStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { ComponentProps } from '../../organisms/HotelInformation/HotelInformation.types';

interface UseConditionalServerDataHotelInformationOptions {
  /**
   * Specify which component is using this hook to determine the correct feature flag
   * 'adf' will check EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND (for AvailabilityCalendar and CalendarWrapper)
   * 'serp' will check EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND (for all other components)
   */
  flagType?: 'adf' | 'serp';
}

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * for HotelInformation serverData based on feature flags, maintaining the same API surface for seamless migration
 */
export const useConditionalServerDataHotelInformation = (
  options: UseConditionalServerDataHotelInformationOptions = { flagType: 'serp' }
): ComponentProps & any => {
  const { flagType = 'serp' } = options;

  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Get data from React Context (fallback)
  const hotelInformationContext = useContext(serverData);

  // Only subscribe to STATE using selectors (for reactivity)
  const hotelInformationData = useHotelInformationServerDataStore(state => state.hotelInformationData);
  const propertyId = useHotelInformationServerDataStore(state => state.propertyId);
  const jsonContent = useHotelInformationServerDataStore(state => state.jsonContent);
  const amenitiesData = useHotelInformationServerDataStore(state => state.amenitiesData);
  const accessibilityData = useHotelInformationServerDataStore(state => state.accessibilityData);
  const generalInformationData = useHotelInformationServerDataStore(state => state.generalInformationData);
  const brandCode = useHotelInformationServerDataStore(state => state.brandCode);
  const keyAmenities = useHotelInformationServerDataStore(state => state.keyAmenities);
  const pageProps = useHotelInformationServerDataStore(state => state.pageProps);
  const isAuthorMode = useHotelInformationServerDataStore(state => state.isAuthorMode);

  // Get methods using selectors (methods are stable, won't cause re-renders)
  const updateServerDataProperty = useHotelInformationServerDataStore(state => state.updateServerDataProperty);
  const updateHotelInformationServerDataState = useHotelInformationServerDataStore(
    state => state.updateHotelInformationServerDataState
  );
  const resetHotelInformationServerDataStore = useHotelInformationServerDataStore(
    state => state.resetHotelInformationServerDataStore
  );

  if (shouldUseZustand) {
    // Return Zustand store data with methods that match HotelInformation serverData Context API
    return {
      hotelInformationData,
      propertyId,
      jsonContent,
      amenitiesData,
      accessibilityData,
      generalInformationData,
      brandCode,
      keyAmenities,
      pageProps,
      isAuthorMode,
      updateServerDataProperty,
      updateHotelInformationServerDataState,
      resetHotelInformationServerDataStore,
    };
  } else {
    // Return React Context data as-is
    return hotelInformationContext || {};
  }
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for HotelInformation serverData
 */
export const useConditionalServerDataHotelInformationADF = () => {
  return useConditionalServerDataHotelInformation({ flagType: 'adf' });
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for HotelInformation serverData
 */
export const useConditionalServerDataHotelInformationSERP = () => {
  return useConditionalServerDataHotelInformation({ flagType: 'serp' });
};
